import { useCallback, useEffect } from 'react';
import { getFriendlyName, getRegisteredIdsFromPort } from 'Lib/WebSerialApi';
import { actions as interfacingActions } from 'Data/ReduxViewModels/EquipmentInterfacing';
import { actions as noticeActions } from 'Data/ReduxViewModels/Notices';
import { useDispatch } from 'react-redux';

/**
 * Hook function that adds/removes a listener for serial port disconnects
 */
export function useSerialPortDisconnectListener() {
  const dispatch = useDispatch();

  /**
   * On disconnect the port becomes unusable so we:
   *  * Unregister the port from any current connections
   *  * Issue a warning that connection was lost
   *
   * To provide a link to reconnect in the notice, we could store the connection options.
   * We'll consider that out of scope until it is asked for.
   */
  const handleDisconnect = useCallback(
    (e: Event) => {
      getRegisteredIdsFromPort(e.target as SerialPort).forEach((id) => {
        dispatch(
          noticeActions.addNotice({
            id: `SerialPortDisconnected-${id}`,
            severity: 'warning',
            message: `The serial port registered for ${getFriendlyName(id)} has been disconnected.`,
          })
        );
        dispatch(interfacingActions.unregisterPort({ equipmentId: id }));
      });
    },
    [dispatch]
  );

  useEffect(() => {
    if (!navigator.serial) return;

    navigator.serial.addEventListener('disconnect', handleDisconnect);

    return () => navigator.serial.removeEventListener('disconnect', handleDisconnect);
  }, [dispatch, handleDisconnect]);
}
